import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useExcRouteV1FixOrderQueryServiceFixOrderGet } from '@interxfi/api-client/generated/hooks/exc-route-v1-fix-order-query-service-fix-order-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table/custom-header';
import { Typography } from '@interxfi/design-system/components/typography';
import type { FilterState } from '@interxfi/table-config/filter';
import type { SortingState } from '@interxfi/table-config/sort';
import {
  ExcRouteV1FixMessageQueryServiceFixMessageListFilterableFields,
  ExcRouteV1FixMessageQueryServiceFixMessageListSortableFields,
  type ExcRouteV1FixOrderState,
} from '@interxfi/types/generated/api';
import { TableSectionHeader } from '../../../../../../../../../../components/table/table-section-header';
import { FixOrderEventDrawer, FixOrderEventSummary } from '../../../../../../../../../../features/fix/order/fix-order-event';
import { getFixOrderStateListItems } from '../../../../../../../../../../features/fix/order/fix-order-list-items';
import { getFixOrderDetailPath } from '../../../../../../../../../../features/fix/order/fix-order-routing';
import { FixOrderStatusPill } from '../../../../../../../../../../features/fix/order/fix-order-status-pill';
import { FixOrderSummaryBar } from '../../../../../../../../../../features/fix/order/fix-order-summary-bar';
import { RouteFixMessagesTable } from '../../../../../../../../../../features/fix/order/message/route-fix-messages-table';
import { OrderSidePill } from '../../../../../../../../../../features/order/order-side-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/oms/fix-order/_fix-order/$fixOrderId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `fix-order-${params.fixOrderId}`,
        label: 'FIX Order',
        to: getFixOrderDetailPath(params.fixOrderId),
        suffix: (
          <CopyButton content={params.fixOrderId} label="Copy FIX Order ID" tooltip={{ copy: params.fixOrderId, copied: 'Copied FIX Order ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('FIX Order'),
  component: FixOrderDetail,
});

function getListItems(data: ExcRouteV1FixOrderState | undefined) {
  const listItems = getFixOrderStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [listItems.data.remainingQuantity],
        },
        {
          id: 'parentOrder',
          title: 'Parent Order',
          items: [
            listItems.data.parentOrder.typeName,
            ...match(listItems.data.parentOrder)
              .with({ customer: P.not(P.nullish) }, ({ customer }) => [customer.value.orderId, customer.value.accountId, customer.value.programId])
              .with({ inventory: P.not(P.nullish) }, ({ inventory }) => [inventory.value.firmOrderId])
              .otherwise(() => []),
          ],
        },
        {
          id: 'instruction',
          title: 'Instruction',
          items: [
            listItems.data.spec.sidePill,
            listItems.data.spec.symbol,
            ...match(listItems.data.spec.amount)
              .with({ '!type': 'quantity' }, (a) => [a.value.value])
              .with({ '!type': 'notional' }, (a) => [a.value.value])
              .otherwise(() => []),
            listItems.data.spec.typeName,
            ...(listItems.data.spec.limitPrice ? [listItems.data.spec.limitPrice] : []),
            listItems.data.spec.timeInForce,
          ] as const satisfies ReadonlyListItem[],
        },
        ...(listItems.data.routingDecision
          ? [
              {
                id: 'routingDecision',
                title: 'Routing Decision',
                items: [
                  listItems.data.routingDecision.ruleId,
                  listItems.data.routingDecision.destinationSessionId,
                  listItems.data.routingDecision.reason,
                  listItems.data.routingDecision.usedFailover,
                  listItems.data.routingDecision.originalSessionId,
                  listItems.data.routingDecision.venueType,
                ],
              },
            ]
          : []),
      ] as const satisfies ReadonlyListGroup[],
      right: [
        listItems.metadata.createdAt,
        listItems.metadata.updatedAt,
        listItems.clOrderId,
        listItems.data.remoteOrderId,
      ] as const satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(fixOrderId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: FixOrderEventDrawer,
        props: { request: { clOrderId: fixOrderId } },
      },
    },
    actions: undefined,
  };
}

const defaultSorts: SortingState<ExcRouteV1FixMessageQueryServiceFixMessageListSortableFields> = [
  {
    id: ExcRouteV1FixMessageQueryServiceFixMessageListSortableFields.DataSent,
    desc: false,
  },
];

function buildFixedFilters(fixOrderId: string): FilterState<ExcRouteV1FixMessageQueryServiceFixMessageListFilterableFields> | undefined {
  return [
    {
      type: {
        filters: [
          {
            id: ExcRouteV1FixMessageQueryServiceFixMessageListFilterableFields.FixOrderId,
            value: { exact: fixOrderId },
          },
        ],
      },
    },
  ];
}

function MessagesTableHeader(props: CustomTableHeaderProps<ExcRouteV1FixMessageQueryServiceFixMessageListFilterableFields, never>) {
  return <TableSectionHeader {...props} filter={null} title="FIX Messages" />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load FIX order', 'view this FIX order');

function FixOrderDetail() {
  const { fixOrderId } = Route.useParams();
  const request = { clOrderId: fixOrderId } as const;
  const fixedFilters = useMemo(() => buildFixedFilters(fixOrderId), [fixOrderId]);
  const { data, error, isPending } = useExcRouteV1FixOrderQueryServiceFixOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.fixOrder), [data?.fixOrder]);
  const menuItems = useMemo(() => getMenuItems(fixOrderId), [fixOrderId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <FixOrderStatusPill isPending={showPending} size="large" status={data?.fixOrder?.status} />
              <OrderSidePill isPending={showPending} side={data?.fixOrder?.data?.spec?.side} size="large" />
            </HorizontalStack>

            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <FixOrderSummaryBar clOrderId={fixOrderId} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <FixOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <RouteFixMessagesTable fixedFilters={fixedFilters} HeaderActions={MessagesTableHeader} initialSort={defaultSorts} />
      </PageLayout.Section>
    </PageLayout>
  );
}
